import Vuex from 'vuex';
import Vue from 'vue';
import getWidth from '../utils/getWidth';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    backdrop: false,
    redirectModal: false,
    expandSidebar: false,
    contactUsWidget: false,
    windowScrolledTo: null,
    headerSearch: false,
    lastFocusedElement: null,
    freezeScroll: false,
    navigation: {
      mobile: false,
      groupId: null,
      subGroupId: null,
    },
    appliedFilterCount: 0,
  },

  getters: {
    navigationMobile: state => state.navigation.mobile,
    navigationGroupId: state => state.navigation.groupId,
    navigationSubGroupId: state => state.navigation.subGroupId,
    headerSearch: state => state.headerSearch,
    backdrop: state => state.backdrop,
    redirectModal: state => state.redirectModal,
    expandSidebar: state => state.expandSidebar,
    contactUsWidget: state => state.contactUsWidget,
    appliedFilterCount: state => state.appliedFilterCount,
  },

  mutations: {
    /* eslint-disable no-param-reassign */
    hideNavigation(state) {
      // Reset everything, hide navigation
      state.navigation.mobile = false;
      state.navigation.groupId = null;
      state.navigation.subGroupId = null;
    },
    setNavigationGroupId(state, id) {
      state.navigation.groupId = id;
    },
    setNavigationSubGroupId(state, id) {
      state.navigation.subGroupId = id;
    },
    toggleNavigationMobile(state, bool) {
      state.navigation.mobile = bool;
    },
    toggleHeaderSearch(state, bool) {
      state.headerSearch = bool;
    },
    setBackdrop(state, bool) {
      state.backdrop = bool;
    },
    setRedirectModal(state, bool) {
      state.redirectModal = bool;
    },
    setExpandSidebar(state, bool) {
      state.expandSidebar = bool;
    },
    setContactUsWidget(state, bool) {
      state.contactUsWidget = bool;
    },
    setWindowScrolledTo(state, scrollAmount) {
      state.windowScrolledTo = scrollAmount;
    },
    setLastFocusedElement(state, element) {
      state.lastFocusedElement = element;
    },
    setFreezeScroll(state, bool) {
      state.freezeScroll = bool;
    },
    setAppliedFilterCount(state, val) {
      state.appliedFilterCount = val;
    },
    /* eslint-enable no-param-reassign */
  },

  actions: {
    hideNavigation(context) {
      context.commit('hideNavigation');
      if (context.state.headerSearch) {
        context.commit('toggleHeaderSearch', false);
      }

      if (context.state.backdrop) {
        context.dispatch('setBackdrop', false);
      }
    },
    setNavigationGroupId(context, id) {
      if (context.state.navigation.groupId === id) {
        // If the current open navigation group is clicked, hide the navigation
        context.dispatch('hideNavigation');
      } else {
        if (!context.state.backdrop) {
          // Set the backdrop to true if its not already shown
          context.dispatch('setBackdrop', true);
        }
        // Set the navigation group
        context.commit('setNavigationGroupId', id);

        if (id !== null && getWidth() >= 992) {
          // If we're on desktop (medium desktops starts at 992px), set the sub group automatically
          context.dispatch('setNavigationSubGroupId', 0);
        }
      }
    },
    setNavigationSubGroupId(context, id) {
      context.commit('setNavigationSubGroupId', id);
    },
    toggleHeaderSearch(context) {
      if (context.state.headerSearch) {
        context.commit('toggleHeaderSearch', false);
        context.dispatch('setBackdrop', false);
        context.dispatch('setFreezeScroll', false);
      } else {
        context.dispatch('hideNavigation');
        context.dispatch('setBackdrop', true);
        context.dispatch('setFreezeScroll', true);
        context.commit('toggleHeaderSearch', true);
      }
    },
    toggleNavigationMobile(context) {
      if (context.state.navigation.mobile) {
        context.dispatch('hideNavigation');
        context.dispatch('setBackdrop', false);
        context.dispatch('setFreezeScroll', false);
      } else {
        if (context.state.headerSearch) {
          context.dispatch('toggleHeaderSearch');
        }
        context.commit('toggleNavigationMobile', true);
        context.dispatch('setBackdrop', true);
        context.dispatch('setFreezeScroll', true);
      }
    },
    setBackdrop(context, bool) {
      context.commit('setBackdrop', bool);

      if (context.state.backdrop) {
        // Save last focused element, so we can focus back on it, when backdrop is gone
        context.commit('setLastFocusedElement', document.activeElement);
      } else {
        if (context.state.headerSearch) {
          context.dispatch('toggleHeaderSearch');
        }
        context.dispatch('hideNavigation');
        context.commit('setExpandSidebar', false);
        context.commit('setContactUsWidget', false);
        context.commit('setRedirectModal', false);
        context.dispatch('setFreezeScroll', false);

        // Set last focused element before backdrop opened to focused element again
        const { lastFocusedElement } = context.state;
        if (lastFocusedElement) {
          lastFocusedElement.focus();
        }
      }
    },
    setExpandSidebar(context, bool) {
      context.commit('setExpandSidebar', bool);

      if (context.state.expandSidebar) {
        context.dispatch('setBackdrop', true);
        context.dispatch('setFreezeScroll', true);
      } else {
        context.dispatch('setBackdrop', false);
        context.dispatch('setFreezeScroll', false);
      }
    },
    setRedirectModal(context, bool) {
      context.commit('setRedirectModal', bool);

      if (context.state.redirectModal) {
        context.dispatch('setBackdrop', true);
        context.dispatch('setFreezeScroll', true);
      } else {
        context.dispatch('setBackdrop', false);
        context.dispatch('setFreezeScroll', false);
      }
    },
    setContactUsWidget(context, bool) {
      context.commit('setContactUsWidget', bool);

      if (context.state.contactUsWidget) {
        context.dispatch('setBackdrop', true);
        context.dispatch('setFreezeScroll', true);
      } else {
        context.dispatch('setBackdrop', false);
        context.dispatch('setFreezeScroll', false);
      }
    },
    setFreezeScroll(context, bool) {
      context.commit('setFreezeScroll', bool);

      if (context.state.freezeScroll) {
        // Freeze scroll, save scroll position
        const scrollAmount = window.scrollY || window.pageYOffset;
        // prettier-ignore
        document.body.setAttribute('style', `position:fixed; overflow-y: scroll; top: -${scrollAmount}px; width: 100%;`);

        context.commit('setWindowScrolledTo', scrollAmount);
      } else {
        // Unfreeze scroll, scroll back to position
        document.body.removeAttribute('style');
        window.scrollTo(0, context.state.windowScrolledTo);
      }
    },
    setAppliedFilterCount(context, val) {
      context.commit('setAppliedFilterCount', val);
    },
  },
});
